<template>
  <div class="perf-page">

    <!-- ─── Hero ─────────────────────────────────────────────────────────── -->
    <header class="perf-page__hero">
      <div class="perf-page__hero-bg"></div>
      <div class="perf-page__hero-glow"></div>
      <div class="perf-page__hero-inner">
        <div class="perf-page__hero-badge">
          <span class="perf-page__hero-badge-dot"><span class="perf-page__hero-badge-ping"></span></span>
          {{ $t('about.perfLabel') }}
        </div>
        <h1 class="perf-page__hero-title">{{ $t('about.perfTitle') }}</h1>
        <p class="perf-page__hero-desc">{{ $t('about.perfSubtitle') }}</p>

        <!-- YTD stats -->
        <div class="perf-page__hero-stats">
          <div class="perf-page__hero-stat perf-page__hero-stat--gold">
            <strong>+{{ ytd.v1 }}%</strong>
            <span>SHC Team V1 · YTD</span>
          </div>
          <div class="perf-page__hero-stat perf-page__hero-stat--green">
            <strong>+{{ ytd.v3 }}%</strong>
            <span>SHC Team V3 · YTD</span>
          </div>
          <div class="perf-page__hero-stat">
            <strong>~{{ ytd.market }}%</strong>
            <span>{{ $t('about.perfMarket') }} · YTD</span>
          </div>
        </div>
      </div>
      <div class="perf-page__hero-wave">
        <svg viewBox="0 0 1440 80" fill="none" preserveAspectRatio="none">
          <path
              d="M0 80L60 65C120 50 240 20 360 10C480 0 600 0 720 7.5C840 15 960 30 1080 37.5C1200 45 1320 45 1380 45L1440 45V80H0Z"
              fill="#f2f0ea"/>
        </svg>
      </div>
    </header>

    <!-- ─── Monthly table + chart ─────────────────────────────────────────── -->
    <section class="perf-page__section perf-page__section--light">
      <div class="perf-page__shell">
        <div class="perf-page__grid">

          <!-- Left: table -->
          <div>
            <div class="perf-page__section-header">
              <h2 class="perf-page__section-title">{{ $t('performance.tableTitle') }}</h2>
              <div class="perf-page__divider"></div>
            </div>

            <div class="perf-page__table">
              <div class="perf-page__table-head">
                <span>{{ $t('home.perfMonth') }}</span>
                <span>SHC Team V1</span>
                <span>SHC Team V3</span>
                <span>{{ $t('home.perfMarket') }}</span>
              </div>
              <div
                  v-for="(row, i) in perfRows"
                  :key="i"
                  :class="['perf-page__table-row', row.highlight ? 'perf-page__table-row--highlight' : '']"
              >
                <span>{{ row.month }}</span>
                <span class="positive">{{ row.v1 }}</span>
                <span class="positive">{{ row.v3 }}</span>
                <span :class="row.marketNeg ? 'negative' : 'positive'">{{ row.market }}</span>
              </div>
            </div>

            <!-- Note -->
            <p class="perf-page__note">{{ $t('performance.tableNote') }}</p>
          </div>

          <!-- Right: chart -->
          <div>
            <div class="perf-page__section-header">
              <h2 class="perf-page__section-title">{{ $t('home.perfChartLabel') }}</h2>
              <div class="perf-page__divider"></div>
            </div>

            <!-- Legend -->
            <div class="perf-page__legend">
              <span class="perf-page__legend-item perf-page__legend-item--v1">SHC Team V1 ({{ ytd.v1 }}%)</span>
              <span class="perf-page__legend-item perf-page__legend-item--v3">SHC Team V3 ({{ ytd.v3 }}%)</span>
              <span class="perf-page__legend-item perf-page__legend-item--mkt">{{
                  $t('about.perfMarket')
                }} (~{{ ytd.market }}%)</span>
            </div>

            <div class="perf-page__chart">
              <div class="perf-page__chart-area">
                <svg
                    :viewBox="`0 0 ${chart.svgW} ${chart.svgH}`"
                    preserveAspectRatio="xMidYMid meet"
                    class="perf-page__svg"
                    @mouseleave="tooltip = null"
                >
                  <defs>
                    <linearGradient id="ppGradV1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#e8c96a"/>
                      <stop offset="100%" stop-color="#c8a246"/>
                    </linearGradient>
                    <linearGradient id="ppGradV3" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#4db87a"/>
                      <stop offset="100%" stop-color="#2f8c56"/>
                    </linearGradient>
                  </defs>

                  <!-- Guide lines -->
                  <line v-for="g in chart.guides" :key="g.val"
                        :x1="chart.padL" :y1="g.y"
                        :x2="chart.svgW - chart.padR" :y2="g.y"
                        :stroke="g.val === 0 ? '#c8bfb4' : '#ede8de'"
                        :stroke-width="g.val === 0 ? 1.5 : 1"
                        :stroke-dasharray="g.val === 0 ? '' : '4 3'"
                  />
                  <text v-for="g in chart.guides" :key="'y'+g.val"
                        :x="chart.padL - 4" :y="g.y + 4"
                        text-anchor="end" font-size="9" fill="#b3aa9f"
                  >{{ g.val }}%
                  </text>

                  <!-- Bars -->
                  <g v-for="(grp, i) in chart.groups" :key="i">
                    <rect :x="grp.v1X" :y="grp.v1Y" :width="chart.barW" :height="grp.v1H"
                          fill="url(#ppGradV1)" rx="3" style="cursor:pointer"
                          @mouseenter="showTip($event, grp.label, 'SHC Team V1', grp.v1Val, '#c8a246')"
                    />
                    <text :x="grp.v1X + chart.barW/2" :y="grp.v1LabelY"
                          text-anchor="middle" font-size="8" font-weight="700" fill="#8b6914"
                    >+{{ grp.v1Val }}%
                    </text>

                    <rect :x="grp.v3X" :y="grp.v3Y" :width="chart.barW" :height="grp.v3H"
                          fill="url(#ppGradV3)" rx="3" style="cursor:pointer"
                          @mouseenter="showTip($event, grp.label, 'SHC Team V3', grp.v3Val, '#2f8c56')"
                    />
                    <text :x="grp.v3X + chart.barW/2" :y="grp.v3LabelY"
                          text-anchor="middle" font-size="8" font-weight="700" fill="#1e6b3e"
                    >+{{ grp.v3Val }}%
                    </text>

                    <rect :x="grp.mktX" :y="grp.mktY" :width="chart.barW" :height="grp.mktH"
                          :fill="grp.isNeg ? '#e07070' : '#b3aa9f'" rx="3" style="cursor:pointer"
                          @mouseenter="showTip($event, grp.label, $t('about.perfMarket'), grp.mktVal, grp.isNeg ? '#e07070' : '#9a9088')"
                    />
                    <text :x="grp.mktX + chart.barW/2" :y="grp.mktLabelY"
                          text-anchor="middle" font-size="8" font-weight="700"
                          :fill="grp.isNeg ? '#c0504d' : '#9a9088'"
                    >{{ grp.mktVal }}%
                    </text>

                    <text :x="grp.cx" :y="grp.labelY"
                          text-anchor="middle" font-size="9" fill="#9a9088"
                    >{{ grp.label }}
                    </text>
                  </g>
                </svg>

                <!-- Tooltip ngoài SVG — không bị clip -->
                <div
                    v-if="tooltip"
                    class="pp-tooltip pp-tooltip--floating"
                    :style="{
                    left: `${tooltip.x - 60}px`,
                    top: `${tooltip.y - 52}px`,
                    borderColor: tooltip.color
                  }"
                >
                  <span class="pp-tooltip__team" :style="{ color: tooltip.color }">{{ tooltip.team }}</span>
                  <span class="pp-tooltip__month">{{ tooltip.label }}</span>
                  <span class="pp-tooltip__val" :style="{ color: tooltip.color }">{{
                      tooltip.val > 0 ? '+' : ''
                    }}{{ tooltip.val }}%</span>
                </div>
              </div>
            </div>

            <!-- 4 stat cards -->
            <div class="perf-page__stats">
              <div class="perf-page__stat">
                <small>SHC TEAM V1</small>
                <strong class="perf-page__stat--gold">{{ ytd.v1 }}%</strong>
                <span>{{ $t('home.perfV1Desc') }}</span>
              </div>
              <div class="perf-page__stat">
                <small>SHC TEAM V3</small>
                <strong class="perf-page__stat--green">{{ ytd.v3 }}%</strong>
                <span>{{ $t('home.perfV3Desc') }}</span>
              </div>
              <div class="perf-page__stat">
                <small>{{ $t('home.perfBestMonth') }}</small>
                <strong>{{ bestMonth.label }}</strong>
                <span>V1 {{ bestMonth.v1 }}% · V3 {{ bestMonth.v3 }}%</span>
              </div>
              <div class="perf-page__stat">
                <small>{{ $t('home.perfMarketLabel') }}</small>
                <strong>~{{ ytd.market }}%</strong>
                <span>{{ $t('home.perfMarketDesc') }}</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- ─── Disclaimer ────────────────────────────────────────────────────── -->
    <section class="perf-page__section perf-page__section--white">
      <div class="perf-page__shell perf-page__shell--narrow">
        <div class="perf-page__disclaimer-box">
          <i class="fas fa-shield-alt"></i>
          <div>
            <h3>{{ $t('performance.disclaimerTitle') }}</h3>
            <p>{{ $t('performance.disclaimerText') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── CTA ───────────────────────────────────────────────────────────── -->
    <section class="perf-page__cta">
      <div class="perf-page__shell">
        <p class="perf-page__cta-text">{{ $t('home.ctaFinalDesc') }}</p>
        <div class="perf-page__cta-actions">
          <button class="perf-page__btn perf-page__btn--primary" @click="$router.push('/contact')">
            {{ $t('home.ctaFinalBtnPrimary') }}
          </button>
          <a href="https://t.me/songhancapital" target="_blank" rel="noopener noreferrer"
             class="perf-page__btn perf-page__btn--outline">
            {{ $t('home.ctaFinalBtnSecondary') }}
          </a>
        </div>
      </div>
    </section>

  </div>
</template>

<script src="./performance.js"></script>
<style src="./performance.scss" lang="scss"></style>
